<script lang="ts">
  import { Episodios, Pacientes, type Episodio } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import { fichaDesdeMotivo, motivoDesdeFicha, filtrarPorMes } from '../lib/motivo-sync';

  type Tipo = Episodio['tipo'];
  let tipo = $state<Tipo>('EMERGENCIA');
  let lista = $state<Episodio[]>([]);
  let historial = $state<Episodio[]>([]);
  let mostrarHistorial = $state(false);
  let filtroMes = $state(new Date().toISOString().slice(0, 7));
  let loading = $state(false);
  let error = $state<string | null>(null);

  let nuevo = $state<Episodio>({ pacienteId: '', tipo: 'EMERGENCIA', motivo: '' });

  const listaFiltrada = $derived(filtrarPorMes(lista, filtroMes, e => e.abiertoEn ?? null));

  async function onPickPaciente(id: string) {
    nuevo.pacienteId = id;
    if (!nuevo.motivo?.trim()) {
      try {
        const p = await Pacientes.uno(id);
        const mot = motivoDesdeFicha(p);
        if (mot) nuevo.motivo = mot;
      } catch { /* opcional */ }
    }
  }

  async function cargar() {
    loading = true; error = null;
    try {
      lista = await Episodios.abiertos(tipo);
      if (mostrarHistorial && filtroMes) {
        historial = await Episodios.historial(tipo, filtroMes);
      } else {
        historial = [];
      }
    }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function abrir(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevo.pacienteId) { error = 'Selecciona paciente.'; return; }
    try {
      await Episodios.abrir({ ...nuevo, tipo: nuevo.tipo || tipo });
      if (nuevo.motivo?.trim()) {
        const partes = fichaDesdeMotivo(nuevo.motivo);
        const p = await Pacientes.uno(nuevo.pacienteId);
        await Pacientes.actualizar(nuevo.pacienteId, {
          ...p,
          motivoConsulta: partes.motivoConsulta || p.motivoConsulta,
          historiaEnfermedadActual: partes.historiaEnfermedadActual || p.historiaEnfermedadActual,
        });
      }
      nuevo = { pacienteId: '', tipo: nuevo.tipo, motivo: '' };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cerrar(id: string) {
    if (!confirm('¿Cerrar episodio?')) return;
    try { await Episodios.cerrar(id); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header class="view-header">
    <div><h2>Episodios</h2><p class="muted">Apertura y cierre; motivo sincronizado con ficha clínica</p></div>
    <div class="row">
      <select bind:value={tipo} onchange={cargar}>
        <option value="EMERGENCIA">Emergencia</option>
        <option value="CONSULTA_EXTERNA">Consulta externa</option>
        <option value="HOSPITALIZACION">Hospitalización</option>
      </select>
      <label class="field mes"><span>Mes</span><input type="month" bind:value={filtroMes} onchange={cargar} /></label>
      <label class="chk"><input type="checkbox" bind:checked={mostrarHistorial} onchange={cargar} /> Historial del mes</label>
      <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
    </div>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}

  <h3>Abrir episodio</h3>
  <form onsubmit={abrir} class="form-grid">
    <PacientePicker bind:value={nuevo.pacienteId} onPick={onPickPaciente} />
    <select bind:value={nuevo.tipo}>
      <option value="EMERGENCIA">Emergencia</option>
      <option value="CONSULTA_EXTERNA">Consulta externa</option>
      <option value="HOSPITALIZACION">Hospitalización</option>
    </select>
    <input placeholder="Motivo (sincroniza con ficha del paciente)" bind:value={nuevo.motivo} />
    <button type="submit" class="full">Abrir</button>
  </form>

  <h3>Abiertos · {tipo.replace('_',' ').toLowerCase()} · {filtroMes}</h3>
  <table>
    <thead><tr><th>Número</th><th>Paciente</th><th>Motivo</th><th>Abierto</th><th>Estado</th><th></th></tr></thead>
    <tbody>
      {#each listaFiltrada as e (e.id)}
        <tr>
          <td><code>{e.numero}</code></td>
          <td><code>{e.pacienteId.slice(0,8)}…</code></td>
          <td>{e.motivo ?? '—'}</td>
          <td>{e.abiertoEn ? new Date(e.abiertoEn).toLocaleString() : '—'}</td>
          <td><span class="badge">{e.estado}</span></td>
          <td><button class="link" onclick={() => cerrar(e.id!)}>Cerrar</button></td>
        </tr>
      {:else}<tr><td colspan="6" class="empty">Sin episodios abiertos en este mes.</td></tr>{/each}
    </tbody>
  </table>

  {#if mostrarHistorial}
    <h3>Historial cerrados · {filtroMes}</h3>
    <table>
      <thead><tr><th>Número</th><th>Paciente</th><th>Motivo</th><th>Abierto</th><th>Cerrado</th><th>Estado</th></tr></thead>
      <tbody>
        {#each historial.filter(e => e.estado !== 'ABIERTO') as e (e.id)}
          <tr>
            <td><code>{e.numero}</code></td>
            <td><code>{e.pacienteId.slice(0,8)}…</code></td>
            <td>{e.motivo ?? '—'}</td>
            <td>{e.abiertoEn ? new Date(e.abiertoEn).toLocaleString() : '—'}</td>
            <td>{e.cerradoEn ? new Date(e.cerradoEn).toLocaleString() : '—'}</td>
            <td><span class="badge muted">{e.estado}</span></td>
          </tr>
        {:else}<tr><td colspan="6" class="empty">Sin episodios cerrados en este mes.</td></tr>{/each}
      </tbody>
    </table>
  {/if}
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  h3 { margin: 1rem 0 .5rem; font-size: 1rem; color: #166534; }
  .row { display: flex; gap: .5rem; flex-wrap: wrap; align-items: flex-end; }
  .field.mes { display: flex; flex-direction: column; gap: .2rem; }
  .field.mes span { font-size: .7rem; font-weight: 600; color: #6b7280; text-transform: uppercase; }
  .chk { display: flex; align-items: center; gap: .35rem; font-size: .85rem; color: #374151; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; }
  .row select, .form-grid input, .form-grid select, .field.mes input { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; }
  .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .5rem; align-items: end; }
  .full { grid-column: span 3; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .5rem .8rem; border-top: 1px solid #f3f4f6; font-size: .9rem; }
  .badge { padding: .15rem .55rem; border-radius: 999px; font-size: .75rem; background: #dcfce7; color: #166534; }
  .badge.muted { background: #f3f4f6; color: #6b7280; }
  .empty { text-align: center; color: #9ca3af; font-style: italic; }
  .err { color: #dc2626; }
  code { background: #f3f4f6; padding: .1rem .35rem; border-radius: 4px; font-size: .8rem; }
  .link { background: transparent; border: 0; color: #dc2626; cursor: pointer; padding: 0; font-weight: 600; }
</style>
